import { Fragment, type ReactNode } from "react"
import { useParams } from "react-router-dom"

/**
 * Remounts a bid-scoped route element whenever the bid changes.
 *
 * React Router renders route elements through an unkeyed chain, so a params-only
 * change keeps the same component instance mounted. Moving between two bids
 * without passing through a list page — the notifications bell (which navigates
 * to a bare `/app/bids/{id}` link) or browser Back/Forward — therefore left the
 * previous bid's component tree alive. Open confirm dialogs, filters and captured
 * entities survived the switch and then acted on the bid they were opened from:
 * a delete confirmed while looking at bid B hard-deleted bid A's response.
 *
 * Keying on `bidId` alone is deliberate. Intra-bid navigation (tabs, sections,
 * artifacts) keeps exactly today's behaviour; only a bid change forces a remount.
 *
 * This lives outside `routes.tsx` so tests can mount it without pulling in the
 * whole route tree (and with it the PDF stack, which jsdom cannot load).
 */
export function BidScoped({ children }: { children: ReactNode }) {
  const { bidId } = useParams()
  return <Fragment key={bidId}>{children}</Fragment>
}
